import { useState, useEffect } from 'react';
import type { TaxYear } from '../../types/tax';
import { TaxDataManager } from '../../utils/taxYear/TaxDataManager';

interface TaxYearSelectorProps {
  className?: string;
}

export default function TaxYearSelector({ className = '' }: TaxYearSelectorProps) {
  const [showComparison, setShowComparison] = useState(false);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const [selectedYear, setSelectedYear] = useState<TaxYear>('2026-27');
  const taxManager = TaxDataManager.getInstance();
  
  useEffect(() => {
    // Initialize with current tax year
    setSelectedYear(taxManager.getCurrentTaxYear());
  }, [taxManager]);
  
  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newYear = event.target.value as TaxYear;
    setSelectedYear(newYear);
    taxManager.setTaxYear(newYear);
  };

  const availableYears = taxManager.getAvailableTaxYears();
  const selectedStrategy = taxManager.getStrategy(selectedYear);
  
  // Show comparison info if not default year
  const showChangesInfo = selectedYear !== '2026-27';
  const changes = showChangesInfo ? taxManager.compareTaxYears('2026-27', selectedYear) : null;

  return (
    <div className={`max-w-4xl mx-auto bg-white dark:bg-slate-800 rounded-lg shadow-md p-4 ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex-1">
          <label htmlFor="tax-year-select" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            Tax Year Selection
          </label>
          <div className="relative">
            <select
              id="tax-year-select"
              value={selectedYear}
              onChange={handleYearChange}
              className="input-field pr-10 appearance-none cursor-pointer"
            >
              {availableYears.map((year) => {
                const strategy = taxManager.getStrategy(year);
                return (
                  <option key={year} value={year}>
                    {strategy.getDisplayName()}
                  </option>
                );
              })}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-slate-300">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
              </svg>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row items-start gap-3 sm:mt-6">
          <div className="text-sm text-gray-600 dark:text-slate-400">
            <span className="font-medium">Period:</span> {selectedStrategy.getStartDate().toLocaleDateString('en-ZA')} - {selectedStrategy.getEndDate().toLocaleDateString('en-ZA')}
          </div>
          
          {showChangesInfo && (
            <button
              onClick={() => setShowComparison(!showComparison)}
              className="text-sm text-sa-green hover:text-sa-green-dark font-medium underline focus:outline-none focus:ring-2 focus:ring-sa-green focus:ring-opacity-50 rounded"
            >
              {showComparison ? 'Hide' : 'Show'} Changes
            </button>
          )}
        </div>
      </div>

      {/* Tax Year Information */}
      <div className="mt-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-md">
        <div className="flex items-start gap-2">
          <div className="flex-shrink-0 relative">
            <button
              onClick={() => setShowInfoTooltip(!showInfoTooltip)}
              className="text-blue-500 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 rounded"
              aria-label="Show tax year information"
              title="Click for more tax year details"
            >
              <svg className="h-5 w-5 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            
            {/* Info Tooltip */}
            {showInfoTooltip && (
              <div className="absolute bottom-full left-0 mb-2 w-72 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg p-4 z-50">
                <div className="text-sm">
                  <h4 className="font-semibold text-charcoal dark:text-slate-100 mb-2">About Tax Years</h4>
                  <p className="text-gray-600 dark:text-slate-300 mb-2">
                    South African tax years run from 1 March to 28 February. Each tax year may have different:
                  </p>
                  <ul className="list-disc list-inside text-gray-600 dark:text-slate-300 space-y-1 mb-3">
                    <li>Tax brackets and rates</li>
                    <li>Primary, secondary, and tertiary rebates</li>
                    <li>Medical aid tax credit amounts</li>
                    <li>Tax-free thresholds</li>
                  </ul>
                  <p className="text-gray-600 dark:text-slate-300 text-xs">
                    Select different tax years above to see how your calculations change with historical or future tax rates.
                  </p>
                </div>
                <button
                  onClick={() => setShowInfoTooltip(false)}
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-700 dark:text-slate-300">
              <span className="font-medium">Currently calculating with:</span> {selectedStrategy.getDisplayName()}
            </p>
            {selectedYear === '2026-27' && (
              <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                <span className="font-medium">✓ Current Tax Year:</span> Applicable from 1 March 2026 to 28 February 2027.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Changes Comparison */}
      {showComparison && changes && (
        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-md">
          <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
            Changes from {taxManager.getStrategy('2026-27').getDisplayName()}
          </h4>
          
          <div className="space-y-2 text-sm">
            <p className="text-blue-800 dark:text-blue-300">{changes.summary}</p>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
              <div className={`flex items-center gap-1 ${changes.brackets ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                <span className="w-2 h-2 rounded-full bg-current"></span>
                <span className="text-xs">Tax Brackets</span>
              </div>
              <div className={`flex items-center gap-1 ${changes.rebates ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                <span className="w-2 h-2 rounded-full bg-current"></span>
                <span className="text-xs">Tax Rebates</span>
              </div>
              <div className={`flex items-center gap-1 ${changes.thresholds ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                <span className="w-2 h-2 rounded-full bg-current"></span>
                <span className="text-xs">Thresholds</span>
              </div>
              <div className={`flex items-center gap-1 ${changes.medicalAidCredits ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
                <span className="w-2 h-2 rounded-full bg-current"></span>
                <span className="text-xs">Medical Aid</span>
              </div>
            </div>
            
            <div className="text-xs text-blue-700 dark:text-blue-300 mt-2">
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-600 dark:bg-green-400"></span>
                No change
              </span>
              <span className="inline-flex items-center gap-1 ml-4">
                <span className="w-2 h-2 rounded-full bg-orange-600 dark:bg-orange-400"></span>
                Changed
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}